import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import SubscriptionStatusBadge from './SubscriptionStatusBadge';
import PlanPricingForm from './PlanPricingForm';
import PlanPriceDisplay from './PlanPriceDisplay';
import { buildPricingPayload, syncDefaultPrices, validatePricingForm } from '../../utils/planPricing';

const DEFAULT_FEATURES = {
  limits: { max_students: 100, max_teachers: 10, max_staff: 5, max_parents: 200, max_classes: 10, max_sections: 20 },
  modules: { students: true, staff: true, attendance: true, fees: true, payroll: false, exams: true, library: false, transport: false, hostel: false, reception: false, reports: true, sms: false, whatsapp: false, mobile_app: false },
  support: { custom_logo: false, white_label: false, custom_domain: false, email_support: true, whatsapp_support: false, priority_support: false },
};

const EMPTY_FORM = {
  name: '',
  slug: '',
  description: '',
  default_price_monthly: 0,
  default_price_half_yearly: 0,
  default_price_yearly: 0,
  price_monthly: 0,
  price_half_yearly: 0,
  price_yearly: 0,
  currency: 'INR',
  trial_days: '',
  is_popular: false,
  status: 'active',
  display_order: 0,
  features: DEFAULT_FEATURES,
};

export default function SubscriptionPlans() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const params = { search, limit: 50 };
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('subscription/plans', { params });
      setItems(res.data?.data?.items || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load plans', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, [search, statusFilter]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, ...syncDefaultPrices(0) });
    setDialogOpen(true);
  };

  const openEdit = (plan) => {
    setEditingId(plan.id);
    setForm({
      ...plan,
      trial_days: plan.trial_days ?? '',
      features: plan.features || DEFAULT_FEATURES,
    });
    setDialogOpen(true);
  };

  const savePlan = async () => {
    const errors = validatePricingForm(form);
    if (errors.length > 0) {
      toast({ title: 'Invalid pricing', description: errors[0], variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        ...buildPricingPayload(form),
        trial_days: form.trial_days === '' ? null : parseInt(form.trial_days, 10),
        is_popular: !!form.is_popular,
      };
      delete payload.pricing;

      if (editingId) {
        await api.put(`subscription/plans/${editingId}`, payload);
        toast({ title: 'Plan updated' });
      } else {
        await api.post('subscription/plans', payload);
        toast({ title: 'Plan created' });
      }
      setDialogOpen(false);
      fetchPlans();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    try {
      await api.delete(`subscription/plans/${id}`);
      toast({ title: 'Plan deleted' });
      fetchPlans();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Delete failed', variant: 'destructive' });
    }
  };

  const setLimit = (key, val) => {
    setForm((f) => ({
      ...f,
      features: { ...f.features, limits: { ...f.features.limits, [key]: parseInt(val, 10) || 0 } },
    }));
  };

  const setModule = (key, val) => {
    setForm((f) => ({
      ...f,
      features: { ...f.features, modules: { ...f.features.modules, [key]: val } },
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Pricing Plans</h1>
          <p className="text-muted-foreground">Manage subscription plans, default prices, and selling prices</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild><Link to="/subscription/analytics">Analytics</Link></Button>
          <Button onClick={openCreate} className="bg-blue-600"><Plus className="w-4 h-4 mr-2" />New Plan</Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input type="search" className="pl-9" placeholder="Search plans…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((plan) => (
            <Card key={plan.id} className={plan.is_popular ? 'border-blue-500' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                  <SubscriptionStatusBadge status={plan.status} />
                </div>
                {plan.is_popular && <span className="text-xs text-blue-600 font-medium">Popular</span>}
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground line-clamp-2">{plan.description}</p>
                <PlanPriceDisplay plan={plan} cycle="monthly" size="sm" />
                <p className="text-xs text-muted-foreground">Up to {plan.features?.limits?.max_students} students</p>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(plan)}><Edit className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => deletePlan(plan.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Plan' : 'Create Plan'}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Slug</Label><Input name="slug" capitalize={false} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

            <PlanPricingForm form={form} onChange={setForm} />

            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>Trial days</Label><Input type="number" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} /></div>
              <div><Label>Display order</Label><Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} /></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2"><Checkbox checked={form.is_popular} onCheckedChange={(v) => setForm({ ...form, is_popular: !!v })} /><Label>Popular plan badge</Label></div>
            <div>
              <Label className="mb-2 block">User limits</Label>
              <div className="grid sm:grid-cols-3 gap-2">
                {['max_students', 'max_teachers', 'max_staff', 'max_parents', 'max_classes', 'max_sections'].map((k) => (
                  <div key={k}><Label className="text-xs">{k.replace('max_', '').replace('_', ' ')}</Label>
                    <Input type="number" value={form.features?.limits?.[k] ?? 0} onChange={(e) => setLimit(k, e.target.value)} /></div>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Modules</Label>
              <div className="grid sm:grid-cols-3 gap-2">
                {Object.keys(form.features?.modules || {}).filter((k) => k !== 'api_access').map((k) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={!!form.features.modules[k]} onCheckedChange={(v) => setModule(k, !!v)} />
                    {k.replace('_', ' ')}
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={savePlan} disabled={saving} className="bg-blue-600">{saving ? 'Saving…' : 'Save Plan'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
